// tabs/skills.ts — client-side port of the shared Skills tab
//   options/game/character_sheet_display/skills/_skills_inc.php (+ div_all_skills_icons_inc.php)
//
// Full render-from-blob (no endpoint): reads computed.creature.skills (keyed by variable_name) and
// paints the SAME two layouts the PHP did — an icon grid (#div_all_skills_icons{sfx}, trained skills
// only, grouped by category) and a detailed table (#div_table_skill_list{sfx}, all skills) — plus the
// Bootstrap nav-tabs that drive the EXISTING global selectCharacterSkillsFilter(sfx, mode)
// (includes/js/ft-inline-helpers.js): pure DOM-class toggling, reused verbatim, NOT re-ported.
//
// Roll wiring is combat-critical and copied EXACTLY: every skill button delegates to useAbility(item_id),
// the same single-arg global the PHP emitted. The two fields NOT on the lean caster blob — the essence
// UUID and the SP→next-tier progress — are derived at RENDER time from the per-request reference catalogs
// (window.SKILL_CATALOG / window.SKILLTIER_CATALOG, rebuilt fresh each page load, NEVER stale), exactly as
// the original PHP did from Skill::ALL / SkillTier::LIST. So the pane is correct on EVERY caster with no
// per-caster blob recompile — nothing skill-specific is baked into the cached blob.
// Icons go through iconPh() (icon_ph twin) so the shared ftMountIcons() pipeline hydrates them — the keys
// (item_id / variable_name / 'skill_tier_N' / essence UUID / 'adv'|'dis') are the SAME keys the PHP passed,
// resolved off the shared ICON_LOOKUP, so the art matches with no server round-trip.

import type { CachedCaster } from '../types';
import { iconPh } from './ability-row';
import { isPlayerCharacter, num } from '../read-helpers';

interface SkillEntry {
    item_id?: string;
    item_name?: string;
    item_type?: string;
    tier_level?: number;
    adv_dis_stacks?: number;
    raw_sp?: { total?: number } & Record<string, unknown>;
    [key: string]: unknown;
}

interface SpProgress { at_max: boolean; pct: number; current_req: number; next_req: number | null }

// Per-request reference catalogs shipped by ft_boot_inc.php (build_js_*_catalog) — rebuilt fresh on
// every page load, so NEVER stale (unlike the cached caster blob). The original PHP read essence + SP
// thresholds from these same catalogs (Skill::ALL / SkillTier::LIST) at render time, so deriving them
// here keeps the pane correct on EVERY caster with no per-caster recompile.
//   SKILL_CATALOG[item_id]      = { item_name, variable_name, item_type, essence_used }
//   SKILLTIER_CATALOG[tier_var] = { item_name, skill_tier_level, variable_name, sp_required }
function skillCatalog(): Record<string, { item_name?: string; item_type?: string; essence_used?: string; variable_name?: string }> {
    return window.SKILL_CATALOG ?? {};
}
function essenceUuidFor(itemId: string, entry: SkillEntry): string {
    return String(skillCatalog()[itemId]?.essence_used ?? entry.essence_used ?? '');
}
// item_name / item_type left the LEAN skills bucket in v22 (build_caster_skills: "the TS skills
// table reads SKILL_CATALOG") — resolve them catalog-first, with the entry fields as a fallback
// for pre-v22 cached blobs that still carry them inline.
function skillNameFor(itemId: string, entry: SkillEntry): string {
    return String(skillCatalog()[itemId]?.item_name ?? entry.item_name ?? '');
}
function skillTypeFor(itemId: string, entry: SkillEntry): string {
    return String(skillCatalog()[itemId]?.item_type ?? entry.item_type ?? '');
}

/** SkillTier rows sorted ascending by level, from SKILLTIER_CATALOG (build_js_skilltier_catalog). */
function skillTierRows(): Array<{ level: number; sp_required: number }> {
    const cat = window.SKILLTIER_CATALOG ?? {};
    const rows = Object.values(cat).map((r) => ({ level: Number(r.skill_tier_level ?? 0), sp_required: Number(r.sp_required ?? 0) }));
    rows.sort((a, b) => a.level - b.level);
    return rows;
}

// get_skill_tier_from_sp() twin (luck_and_skill_roll_helpers.php) — highest tier whose sp_required is met.
function skillTierLevelFromSp(totalSp: number): number {
    if (totalSp <= 0) return 0;
    const rows = skillTierRows();
    for (let i = rows.length - 1; i >= 0; i--) {
        const row = rows[i];
        if (row && totalSp >= row.sp_required) return row.level;
    }
    return 1;
}

// get_skill_sp_progress_to_next_tier() twin — render-time SP→next-tier progress (matches the PHP exactly).
// Returns null only if the catalog is unavailable (caller then falls back to '—').
function skillSpProgress(totalSp: number): SpProgress | null {
    const rows = skillTierRows();
    if (rows.length === 0) return null;
    totalSp = Math.max(0, totalSp);
    const level = skillTierLevelFromSp(totalSp);
    const byLevel = new Map<number, number>();
    let maxLevel = 0;
    for (const r of rows) {
        byLevel.set(r.level, r.sp_required);
        if (r.level > maxLevel) maxLevel = r.level;
    }
    const curReq = byLevel.get(level) ?? 0;
    if (level >= maxLevel) return { at_max: true, pct: 100, current_req: curReq, next_req: null };
    const nextReq = byLevel.get(level + 1) ?? 0;
    const span = nextReq - curReq;
    if (span <= 0) return { at_max: true, pct: 100, current_req: curReq, next_req: null };
    const frac = (Math.min(totalSp, nextReq) - curReq) / span;
    const pct = Math.max(0, Math.min(100, Math.floor(100 * frac)));
    return { at_max: false, pct, current_req: curReq, next_req: nextReq };
}

// Category groups for the icon grid — order + label-icon key + group-label "info" ability UUID mirror
// div_all_skills_icons_inc.php. Defensive/innate group labels are inert (no onclick); the rest open an
// item-info help ability via useAbility (the hardcoded UUIDs are copied verbatim from the PHP).
const SKILL_GROUPS: ReadonlyArray<{ type: string; labelIcon: string; info: string | null }> = [
    { type: 'defensive_skill', labelIcon: 'defensive_skills', info: null },
    { type: 'innate_skill', labelIcon: 'innate_skills', info: null },
    { type: 'gathering_skill', labelIcon: 'gathering_skills', info: '22918408-67CC-4093-80E8-BF6FA8915C21' },
    { type: 'knowledge_skill', labelIcon: 'knowledge_skills', info: '63ABC96E-3804-4E1F-B38D-AABA3237B2D8' },
    { type: 'crafting_skill', labelIcon: 'crafting_skills', info: '72F93730-134F-4CA2-8FC4-F2915F20CAC6' },
    { type: 'language_skill', labelIcon: 'language_skills', info: '63ABC96E-3804-4E1F-B38D-AABA3237B2D8' },
    { type: 'instrument_classification_skill', labelIcon: 'instrument_classification_skills', info: '22918408-67CC-4093-80E8-BF6FA8915C21' },
    { type: 'weapon_skill', labelIcon: 'weapon_skills', info: '22918408-67CC-4093-80E8-BF6FA8915C21' },
    { type: 'armor_skill', labelIcon: 'armor_skills', info: '22918408-67CC-4093-80E8-BF6FA8915C21' },
];

// Nav-tabs: {filter mode, icon key}. Modes are the verbatim selectCharacterSkillsFilter() args the PHP
// used (mode 'X' shows rows classed cl_tr_X_skill; 'all'/'icons' are the two layout toggles).
const NAV_TABS: ReadonlyArray<{ mode: string; icon: string }> = [
    { mode: 'icons', icon: 'all_skills_icons' },
    { mode: 'all', icon: 'all_skills_list' },
    { mode: 'defensive', icon: 'defensive_skills' },
    { mode: 'innate', icon: 'innate_skills' },
    { mode: 'gathering', icon: 'gathering_skills' },
    { mode: 'knowledge', icon: 'knowledge_skills' },
    { mode: 'crafting', icon: 'crafting_skills' },
    { mode: 'language', icon: 'language_skills' },
    { mode: 'instrument_classification', icon: 'instrument_classification_skills' },
    { mode: 'weapon', icon: 'weapon_skills' },
    { mode: 'armor', icon: 'armor_skills' },
];

// Monotonic per-mount counter → guarantees a unique DOM suffix even when the SAME caster's skills pane
// is mounted twice on one page (main sheet + a side-info copy), which is exactly the duplicate-id /
// cross-wired-filter case the PHP avoided with distinct pc_dom_suffix vs ec_dom_suffix. The suffix is
// internally consistent within one render (nav ids + grid/list ids + the selectCharacterSkillsFilter
// onclick arg all share it). It is fixed per MOUNT: a repaint into the same container reuses it, so an
// unchanged skills pane builds identical markup and the sheet's paint guard keeps it (and its open tab).
let paneSeq = 0;
const mountSeq = new WeakMap<HTMLElement, number>();

type SkillsWindow = Record<string, unknown>;

function skillCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as SkillsWindow)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

function skillAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-skill-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        // HTML parsers lowercase attribute names, so emitting `data-ft-itemId` makes the
        // value available as dataset.ftItemid rather than dataset.ftItemId. Keep the
        // markup kebab-cased so every delegated handler receives its UUID/arguments.
        const attrKey = key.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
        pairs.push(`data-ft-${attrKey}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function skillInfoAttrs(itemId: string): string {
    return skillAttrs('open-info', { itemId });
}

function skillUseAttrs(itemId: string): string {
    return skillAttrs('use-ability', { itemId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-skill-action]')
        : null;
    if (!el) return;
    const action = el.dataset.ftSkillAction || '';
    if (action === 'use-ability') {
        event.preventDefault();
        // Same fold-out targeting lane as a slot rune (ftDispatchAbilityClick, action-bar.ts): a contested
        // skill arms creature targeting on the FT3D dock; anything else falls through to the immediate roll.
        const casterId = el.closest<HTMLElement>('[data-ft-caster-id]')?.getAttribute('data-ft-caster-id') || '';
        if (skillCall('ftDispatchAbilityClick', casterId, el.dataset.ftItemId || '') === true) return;
        skillCall('useAbility', el.dataset.ftItemId || '');
        return;
    }
    if (action === 'open-info') {
        event.preventDefault();
        skillCall('openItemInfo', el.dataset.ftItemId || '');
        return;
    }
    if (action === 'advdis-info') {
        event.preventDefault();
        skillCall('forceOpenSideInfoPanel');
        skillCall('renderSkillAdvDisInfoPanel', el.dataset.ftCasterId || '', el.dataset.ftItemId || '');
        return;
    }
    if (action === 'action-bar-skill-type') {
        event.preventDefault();
        skillCall('ftActionBarSkillType', el.dataset.ftCasterId || '', el.dataset.ftSkillType || '');
        return;
    }
    if (action === 'action-bar-show-all') {
        event.preventDefault();
        skillCall('ftActionBarSkillsShowAll', el.dataset.ftCasterId || '');
        return;
    }
    if (action === 'filter') {
        event.preventDefault();
        skillCall('selectCharacterSkillsFilter', el.dataset.ftSuffix || '', el.dataset.ftMode || '');
    }
});

/** AdvDis::getIcon twin (includes/additions.php) — only reached when stacks !== 0. */
function advDisStacksIcon(stacks: number): string {
    if (stacks >= 2) return iconPh(`adv_${stacks}`);
    if (stacks === 1) return iconPh('adv');
    if (stacks <= -2) return iconPh(`dis_${Math.abs(stacks)}`);
    if (stacks === -1) return iconPh('dis');
    return iconPh('dice-d20');
}

export function skillsOf(caster: CachedCaster): Record<string, SkillEntry> {
    const raw = caster.computed?.creature?.skills ?? caster.skills;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const out: Record<string, SkillEntry> = {};
    for (const [key, value] of Object.entries(raw)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) out[key] = value as SkillEntry;
    }
    return out;
}

/** Ordered [variable_name, entry] pairs, dropping the non-skill _meta key. */
function storedSkillEntries(caster: CachedCaster): Array<[string, SkillEntry]> {
    return Object.entries(skillsOf(caster)).filter(([k, v]) => k !== '_meta' && v && typeof v === 'object') as Array<[string, SkillEntry]>;
}

// v33 (ROUND-12 #1): pure default-flawed entries are ELIDED from the blob, and stored entries
// carry no item_id (the bucket key/variable_name is the join key). UNION the per-request
// SKILL_CATALOG — in Skill::ALL insertion order, the same order the blob used to carry — so
// uninvested skills still RENDER as flawed rows, synthesizing the build-time default
// (tier 0 flawed; tier 1 for defensive/innate, mirroring build_caster_skills) and stamping
// item_id back from the catalog key. An EMPTY bucket means "skills never compiled" → no
// synthetic rows (pre-v33 warm blobs pass through unchanged: every var is already stored).
function skillEntries(caster: CachedCaster): Array<[string, SkillEntry]> {
    const stored = storedSkillEntries(caster);
    if (stored.length === 0) return stored;
    const byVar = new Map(stored);
    const out: Array<[string, SkillEntry]> = [];
    const seen = new Set<string>();
    for (const [itemId, cat] of Object.entries(skillCatalog())) {
        const variable = String(cat.variable_name ?? '');
        if (variable === '') continue;
        seen.add(variable);
        const defensive = cat.item_type === 'defensive_skill' || cat.item_type === 'innate_skill';
        const entry = byVar.get(variable)
            ?? { tier_level: defensive ? 1 : 0, sources: [defensive ? 'default_defensive' : 'default_flawed'] } as SkillEntry;
        out.push([variable, entry.item_id ? entry : { ...entry, item_id: itemId }]);
    }
    // Stored vars the catalog doesn't know (defensive: never drop data we have) — also the
    // whole list when SKILL_CATALOG is unavailable on a page (out stays empty, stored wins).
    for (const [variable, entry] of stored) {
        if (!seen.has(variable)) out.push([variable, entry]);
    }
    return out;
}

// ── icon-grid skill button (render_skill_icon_button twin) ──
function gridSkillButton(variable: string, entry: SkillEntry, canUse: boolean): string {
    const itemId = String(entry.item_id ?? '');
    const sid = escapeHtml(itemId);
    const tierLevel = Number(entry.tier_level ?? 0);
    const tier = `skill_tier_${tierLevel}`;
    const advDis = Number(entry.adv_dis_stacks ?? 0);

    // Tier badge — TOP-LEFT corner (frees top-right for the ADV/DIS cue).
    const badge = tierLevel > 0
        ? `<span class="position-absolute top-0 start-0 translate-middle">${iconPh(tier, 20, 20)}</span>`
        : '';

    // ADV/DIS indicator — TOP-CENTER (over the button's own icon, not the right edge) so it no longer
    // spills onto the neighbouring skill button. Coloured halo + count pill at 2+ stacks. pointer-events:none
    // so a tap still rolls the skill through the button underneath.
    let advDisHtml = '';
    if (advDis !== 0) {
        const isAdv = advDis > 0;
        const count = Math.abs(advDis);
        const glow = isAdv ? 'rgba(13,110,253,.95)' : 'rgba(220,53,69,.95)';
        let pill = '';
        if (count >= 2) {
            const pillBg = isAdv ? 'linear-gradient(135deg,#3d8bfd,#0d6efd)' : 'linear-gradient(135deg,#e4606d,#dc3545)';
            pill = `<span class="badge rounded-pill border border-light shadow-sm" style="background:${pillBg};color:#fff;font-size:0.62rem;font-weight:600;line-height:1;padding:0.12rem 0.34rem;margin-top:-0.12rem;">${count}</span>`;
        }
        const title = escapeHtml(`${isAdv ? 'Advantage' : 'Disadvantage'}${count >= 2 ? ' ×' + count : ''}`);
        advDisHtml = `<span class="position-absolute top-0 start-50 translate-middle d-flex flex-column align-items-center" style="z-index:3;pointer-events:none;" title="${title}">`
            + `<span class="d-inline-flex align-items-center justify-content-center" style="filter:drop-shadow(0 0 1.5px ${glow}) drop-shadow(0 0 1.5px ${glow});">${iconPh(isAdv ? 'adv' : 'dis', 18, 18)}</span>`
            + `${pill}</span>`;
    }

    const buttonIcon = iconPh(variable || itemId);
    if (canUse) {
        // data-ft-info-item-id makes this a press-hold target (press-hold.ts): a ~450ms hold opens the
        // skill's item-info (openItemInfo → DB pool on a cache miss) and swallows the trailing click, so
        // a tap still rolls via useAbility. Same tap-rolls / hold-shows-info gesture as ability buttons.
        return `<div data-ft-skill-grid-variable="${escapeHtml(variable)}" class="pl-3 pr-3"><button type="button" class="btn btn-sm btn-primary position-relative" ${skillUseAttrs(itemId)} data-ft-info-item-id="${sid}">${badge}${advDisHtml}${buttonIcon}</button></div>`;
    }
    return `<div data-ft-skill-grid-variable="${escapeHtml(variable)}" class="pl-3 pr-3"><div class="btn btn-sm btn-primary position-relative user-select-none opacity-75" style="pointer-events: none; cursor: default;" role="img" aria-hidden="true">${badge}${advDisHtml}${buttonIcon}</div></div>`;
}

// ── icon grid (div_all_skills_icons_inc.php twin) ──
function iconGrid(caster: CachedCaster, sfx: string, canUseGrid: boolean): string {
    // Bucket trained skills by item_type once, preserving Skill::ALL (blob insertion) order.
    const byType = new Map<string, Array<[string, SkillEntry]>>();
    for (const [variable, entry] of skillEntries(caster)) {
        if (Number(entry.tier_level ?? 0) === 0) continue; // is_flawed → hidden in the grid
        const t = skillTypeFor(String(entry.item_id ?? ''), entry);
        if (!byType.has(t)) byType.set(t, []);
        byType.get(t)!.push([variable, entry]);
    }

    let groups = '';
    for (const g of SKILL_GROUPS) {
        const skills = byType.get(g.type) ?? [];
        let buttons = '';
        for (const [variable, entry] of skills) buttons += gridSkillButton(variable, entry, canUseGrid);

        // Blanket language knowledge (2026-07-15) surfaces INSIDE the Languages group, because it is
        // the flag alternative to a per-language skill button — a creature/PC carrying it may have few
        // or zero individual language skills, which would otherwise render an empty Languages row that
        // reads as "knows no languages" when the truth is the opposite.
        if (g.type === 'language_skill') {
            if (num(ftLayerValue(caster, 'speaks_all_languages')) !== 0) {
                buttons += `<span class="badge bg-dark border m-1 d-inline-flex align-items-center gap-1" title="Speaks and understands every language.">${iconPh('speaks_all_languages')} All</span>`;
            }
            if (num(ftLayerValue(caster, 'reads_all_languages')) !== 0) {
                buttons += `<span class="badge bg-light text-dark border m-1 d-inline-flex align-items-center gap-1" title="Reads every written language.">${iconPh('reads_all_languages')} All</span>`;
            }
        }

        // Group label button: inert for defensive/innate; useAbility(info) for the rest, gated like the PHP.
        let label: string;
        if (g.info === null) {
            label = `<button class="btn btn-sm btn-secondary ft-skill-group-btn" type="button">${iconPh(g.labelIcon)}</button>`;
        } else if (canUseGrid) {
            label = `<button class="btn btn-sm btn-secondary ft-skill-group-btn" type="button" ${skillUseAttrs(g.info)}>${iconPh(g.labelIcon)}</button>`;
        } else {
            label = `<button class="btn btn-sm btn-secondary ft-skill-group-btn" type="button" style="pointer-events: none; cursor: default; opacity: 0.8;" disabled>${iconPh(g.labelIcon)}</button>`;
        }

        groups += `<div data-ft-skill-group="${escapeHtml(g.type)}" class="d-flex align-items-stretch border-bottom p-1 mt-1 mb-1 ft-skill-group-row">`
            + `<div class="ft-skill-group-label border-end py-1 px-1 d-flex align-items-center flex-shrink-0">${label}</div>`
            + `<div class="ft-skill-group-skills d-flex flex-wrap flex-grow-1 min-w-0 justify-content-start align-items-center align-content-start gap-1 py-1 ps-1 pe-0">${buttons}</div>`
            + `</div>`;
    }
    return `<div id="div_all_skills_icons${sfx}">${groups}</div>`;
}

// ── planned-but-locked skill tiers ───────────────────────────────────────────────────────────────
// A user-choice pick whose CONTAINER outranks the character's level is deliberately withheld by
// process_user_choices_layers() — the ancestry tables let you choose your level-1/5/10 boons up
// front, and a level-10 one must not apply at level 3. The sheet used to show nothing at all for
// those, so a player who picked "Eyes of the Beast" saw a silently absent ability and read it as a
// broken layer (owner report 2026-07-28). The compile records them display-only in
// computed.creature.planned_user_choices; this paints the same muted "<tier> @ <level>" affordance
// the character creator shows, so the sheet and the creator finally agree.
//
// DISPLAY ONLY — never folded into tier_level, which would apply a pick the engine withheld.
interface PlannedUserChoice {
    item_id?: string;
    item_name?: string;
    requires_level?: number;
    skill_grants?: Array<{ s?: string; t?: number }>;
}

function plannedTierFor(caster: CachedCaster, skillItemId: string): { tier: number; level: number } | null {
    if (!skillItemId) return null;
    const rows = (caster as unknown as {
        computed?: { creature?: { planned_user_choices?: unknown } };
    })?.computed?.creature?.planned_user_choices;
    if (!Array.isArray(rows)) return null;

    const want = skillItemId.toUpperCase();
    let best: { tier: number; level: number } | null = null;
    for (const raw of rows) {
        const row = raw as PlannedUserChoice;
        const level = Number(row?.requires_level ?? 0) || 0;
        const grants = Array.isArray(row?.skill_grants) ? row.skill_grants : [];
        // A cached/corrupt blob can carry Infinity, negatives, or fractions here; those would paint a
        // nonsense badge ("Infinity @ -3"). Require clean positive integers (Codex review 2026-07-29).
        if (!Number.isInteger(level) || level <= 0) continue;
        for (const g of grants) {
            if (String(g?.s ?? '').toUpperCase() !== want) continue;
            const tier = Number(g?.t ?? 0) || 0;
            if (!Number.isInteger(tier) || tier <= 0) continue;
            // Highest tier wins; on a tie advertise the one you unlock SOONEST.
            if (!best || tier > best.tier || (tier === best.tier && level < best.level)) {
                best = { tier, level };
            }
        }
    }
    return best;
}

// ── detailed list row (_skills_inc.php tbody twin) ──
function listRow(caster: CachedCaster, casterId: string, variable: string, entry: SkillEntry, canEdit: boolean, isPc: boolean): string {
    const itemId = String(entry.item_id ?? '');
    const sid = escapeHtml(itemId);
    const itemType = skillTypeFor(itemId, entry); // render-time, from SKILL_CATALOG (never stale)
    const essenceUsed = essenceUuidFor(itemId, entry);
    const name = skillNameFor(itemId, entry);
    const tierLevel = Number(entry.tier_level ?? 0);
    const tier = `skill_tier_${tierLevel}`;
    const advDis = Number(entry.adv_dis_stacks ?? 0);
    const spTotal = Number(entry.raw_sp?.total ?? 0);
    // SP→next-tier progress computed at render time from SKILLTIER_CATALOG (matches the old PHP, which
    // also computed it per-render); players only, every skill — incl. a 0%-toward-tier-1 bar at 0 SP.
    const prog = isPc ? skillSpProgress(spTotal) : null;

    // Roll button — data-ft-info-item-id makes it a press-hold target (see gridSkillButton): hold opens
    // the skill info, tap rolls via useAbility. The trailing click is swallowed by press-hold.ts on a hold.
    const rollCell = canEdit
        ? `<button type="button" class="btn btn-primary" ${skillUseAttrs(itemId)} data-ft-info-item-id="${sid}">${iconPh(itemId)}</button>`
        : `<span class="btn btn-primary pe-none user-select-none disabled opacity-75" style="pointer-events: none; cursor: default;" aria-hidden="true">${iconPh(itemId)}</span>`;

    // Name
    const nameCell = canEdit
        ? `<span role="button" class="pl-2" ${skillInfoAttrs(itemId)}>${escapeHtml(name)}</span>`
        : `<span class="pl-2 user-select-none">${escapeHtml(name)}</span>`;

    // Essence
    const essenceCell = canEdit
        ? `<span role="button" ${skillInfoAttrs(essenceUsed)}>${iconPh(essenceUsed)}</span>`
        : `<span class="user-select-none opacity-75" aria-hidden="true">${iconPh(essenceUsed)}</span>`;

    // Adv/Dis
    let advDisCell = '';
    if (advDis !== 0) {
        if (casterId !== '' && checkIfValidUUID(casterId)) {
            const t = advDis > 0 ? 'Why advantage stacks' : 'Why disadvantage stacks';
            const aria = advDis > 0 ? 'Show advantage breakdown' : 'Show disadvantage breakdown';
            advDisCell = `<button type="button" class="btn btn-link p-0 align-baseline ft-skill-sheet-advdis-btn shadow-none border-0 text-decoration-none" title="${escapeHtml(t)}" aria-label="${escapeHtml(aria)}" ${skillAttrs('advdis-info', { casterId, itemId })}>${advDisStacksIcon(advDis)}</button>`;
        } else {
            advDisCell = advDisStacksIcon(advDis);
        }
    }

    // SP progress bar
    let progCell = '—';
    if (isPc && prog) {
        if (prog.at_max) {
            const barTitle = `${spTotal} SP — maximum tier from skill points`;
            progCell = progBar(100, barTitle);
        } else {
            const pct = Number(prog.pct ?? 0);
            const barTitle = `${spTotal} / ${Number(prog.next_req ?? 0)} SP · ${pct}% toward next tier`;
            progCell = progBar(pct, barTitle);
        }
    }

    // Planned-but-locked grant (see plannedTierFor): only worth showing when it would actually raise
    // this skill above where it stands today, otherwise it is noise.
    const planned = plannedTierFor(caster, itemId);
    const plannedBadge = (planned && planned.tier > tierLevel)
        ? `<span class="badge bg-secondary ms-1 ft-pc-planned-tier" title="${escapeHtml(
            `Granted by a choice that unlocks at level ${planned.level}`,
        )}">${escapeHtml(String(planned.tier))} @ ${escapeHtml(String(planned.level))}</span>`
        : '';

    // Skill-point columns are a PLAYER-only concept — encounter creatures / monsters don't allocate SP,
    // so the two SP cells are carved out entirely (not rendered as '—') for non-PCs. Header matches.
    const spCells = isPc
        ? `<td class="text-center">${escapeHtml(String(spTotal))}</td><td class="text-center px-2">${progCell}</td>`
        : '';
    const grantNames = Object.values(entry.tier_grant_sources ?? {}).map(source =>
        source && typeof source === 'object' ? String((source as Record<string, unknown>).label ?? '') : '').filter(Boolean);
    const grantTitle = grantNames.length ? ` title="${escapeHtml('Granted by ' + [...new Set(grantNames)].join(', '))}"` : '';

    return `<tr data-ft-skill-variable="${escapeHtml(variable)}" class="cl_tr_all_skills cl_tr_${escapeHtml(itemType)}">`
        + `<td class="text-center"><div class="p-2">${rollCell}</div></td>`
        + `<td class="text-left">${nameCell}</td>`
        + `<td class="text-center">${iconPh(itemType)}</td>`
        + `<td class="text-center">${essenceCell}</td>`
        + `<td class="text-center">${advDisCell}</td>`
        + spCells
        + `<td class="text-center"${grantTitle}>${iconPh(tier)}${plannedBadge}</td>`
        + `</tr>`;
}

function progBar(pct: number, title: string): string {
    const tip = escapeHtml(title);
    return `<div class="w-100 py-1" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-custom-class="skill-sp-tooltip-wide" data-bs-delay='{"show":0,"hide":0}' data-bs-title="${tip}">`
        + `<div class="progress" style="height: 10px;"><div class="progress-bar bg-info" role="progressbar" style="width: ${pct}%;" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100" aria-label="${tip}"></div></div>`
        + `</div><div class="skill-sp-progress-caption">${escapeHtml(title)}</div>`;
}

// ── detailed list (#div_table_skill_list, hidden by default) ──
function detailList(caster: CachedCaster, casterId: string, sfx: string, canEdit: boolean, isPc: boolean): string {
    let rows = '';
    for (const [variable, entry] of skillEntries(caster)) rows += listRow(caster, casterId, variable, entry, canEdit, isPc);

    const id = `div_table_skill_list${sfx}`;
    const style = `<style>
#${id} .skill-sp-progress-caption { display: none !important; }
@media (any-hover: none) {
    #${id} .skill-sp-progress-caption { display: block !important; font-size: 0.75rem; line-height: 1.2; margin-top: 0.125rem; color: var(--bs-secondary-color); text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}
.tooltip.skill-sp-tooltip-wide .tooltip-inner { max-width: min(26rem, 92vw); white-space: normal; text-align: center; }
#${id} .ft-skill-sheet-advdis-btn.btn-link::after { display: none; content: none; }
</style>`;

    // SP-point + to-next-tier headers render only for players (carved out for creatures — see listRow).
    const spHeaders = isPc
        ? `<th class="text-center" style="width: 72px;">${iconPh('skill_points')}</th>`
            + `<th class="text-center" title="Invested skill points toward the next tier threshold"><span class="d-inline-flex align-items-center justify-content-center gap-1 flex-nowrap text-nowrap text-muted small">${iconPh('to_next_tier', 20, 20)}<span>To Next Tier</span></span></th>`
        : '';
    const header = `<thead><tr>
<th class="text-center" style="width: 90px;"><span role="button" ${skillInfoAttrs('E8E3A972-A17D-9144-DADB-7115A3BF11BD')}>${iconPh('skill')}</span></th>
<th></th>
<th class="text-center" style="width: 90px;"><span role="button" ${skillInfoAttrs('0BAB595F-6283-BC3C-A732-230308878061')}>${iconPh('skill_type')}</span></th>
<th class="text-center" style="width: 90px;"><span role="button" ${skillInfoAttrs('E8E3A972-A17D-9144-DADB-7115A3BF11BD')}>${iconPh('essence')}</span></th>
<th class="text-center" style="width: 90px;"><span role="button" ${skillInfoAttrs('E8E3A972-A17D-9144-DADB-7115A3BF11BD')}>${iconPh('adv_dis')}</span></th>
${spHeaders}
<th class="text-center" style="width: 90px;"><span role="button" ${skillInfoAttrs('02697138-9C12-4B29-ACD0-1DEB6258D849')}>${iconPh('skill_tier')}</span></th>
</tr></thead>`;

    return `<div id="${id}" style="display:none;">${style}<table class="table table-hover table-sm align-middle">${header}<tbody>${rows}</tbody></table></div>`;
}

// ── action-bar Skills quick-tray (two-tier fold-out, modeled on the ability island) ──
// BOTTOM row = a skill-TYPE selector (innate, defensive, …); clicking one folds out that type's skill chips
// in the row ABOVE it. Innate is the default selection. Each chip rolls via useAbility, is draggable onto the
// bar (slot_type 'skill'), opens its info on press-hold, and POPS IN its ADV/DIS + tier badges on hover.

// Type-row order (innate first, then the rest of the SKILL_GROUPS order) + human labels / label-icon keys.
export const DEFAULT_SKILL_TYPE = 'innate_skill';
const SKILL_TYPE_ORDER: ReadonlyArray<string> = ['innate_skill', ...SKILL_GROUPS.map((g) => g.type).filter((t) => t !== 'innate_skill')];
const SKILL_TYPE_ICON: Record<string, string> = Object.fromEntries(SKILL_GROUPS.map((g) => [g.type, g.labelIcon]));
const SKILL_TYPE_LABELS: Record<string, string> = {
    innate_skill: 'Innate', defensive_skill: 'Defensive', gathering_skill: 'Gathering',
    knowledge_skill: 'Knowledge', crafting_skill: 'Crafting', language_skill: 'Languages',
    instrument_classification_skill: 'Instruments', weapon_skill: 'Weapons', armor_skill: 'Armor',
};

/** Bucket the caster's skills by item_type. By default only TRAINED skills (tier > 0, like the icon grid);
 *  `includeUntrained` also keeps tier-0 (flawed) skills — the "Show all" view, where untrained skills roll
 *  at disadvantage and are flagged red. */
function skillsByType(caster: CachedCaster, includeUntrained = false): Map<string, Array<[string, SkillEntry]>> {
    const byType = new Map<string, Array<[string, SkillEntry]>>();
    for (const [variable, entry] of skillEntries(caster)) {
        if (!includeUntrained && Number(entry.tier_level ?? 0) === 0) continue;
        const t = skillTypeFor(String(entry.item_id ?? ''), entry);
        if (!byType.has(t)) byType.set(t, []);
        byType.get(t)!.push([variable, entry]);
    }
    return byType;
}

/** The skill types that actually have trained skills, in type-row order (unknown types appended). */
function availableSkillTypes(byType: Map<string, Array<[string, SkillEntry]>>): string[] {
    const out: string[] = [];
    for (const t of SKILL_TYPE_ORDER) if (byType.has(t)) out.push(t);
    for (const t of byType.keys()) if (!out.includes(t)) out.push(t);
    return out;
}

/** True when the caster has any skill at all (drives whether the action-bar Skills launcher renders). Includes
 *  untrained, so a caster with only flawed skills can still open the tray and "Show all" them. */
export function casterHasSkills(caster: CachedCaster): boolean {
    return skillsByType(caster, true).size > 0;
}

/** Hover-revealed cluster on a chip: the skill-tier badge (top-left) + ADV/DIS cue (top-center), mirroring the
 *  Skills-grid button. Hidden by default; CSS pops it in on chip hover. '' when there's nothing to show. */
function skillChipHoverBadges(entry: SkillEntry): string {
    const tierLevel = Number(entry.tier_level ?? 0);
    const advDis = Number(entry.adv_dis_stacks ?? 0);
    let inner = '';
    if (tierLevel > 0) {
        inner += `<span class="ft-action-skill-chip__tier">${iconPh('skill_tier_' + tierLevel, 18, 18, false)}</span>`;
    }
    if (advDis !== 0) {
        const isAdv = advDis > 0;
        const count = Math.abs(advDis);
        const glow = isAdv ? 'rgba(13,110,253,.95)' : 'rgba(220,53,69,.95)';
        let pill = '';
        if (count >= 2) {
            const pillBg = isAdv ? 'linear-gradient(135deg,#3d8bfd,#0d6efd)' : 'linear-gradient(135deg,#e4606d,#dc3545)';
            pill = `<span class="badge rounded-pill border border-light shadow-sm" style="background:${pillBg};color:#fff;font-size:0.58rem;font-weight:600;line-height:1;padding:0.08rem 0.28rem;margin-top:-0.1rem;">${count}</span>`;
        }
        inner += `<span class="ft-action-skill-chip__advdis d-flex flex-column align-items-center" style="filter:drop-shadow(0 0 1.5px ${glow}) drop-shadow(0 0 1.5px ${glow});">${iconPh(isAdv ? 'adv' : 'dis', 16, 16, false)}${pill}</span>`;
    }
    if (!inner) return '';
    return `<span class="ft-action-skill-chip__hov" aria-hidden="true">${inner}</span>`;
}

/** One skill chip: the skill icon (name as a bs-tooltip via iconPh title) + hover badges. Carries
 *  data-ft-info-item-id so it doubles as a press-hold info target AND a drag SOURCE the action bar binds as
 *  slot_type 'skill' (onAbilityPointerDown matches any [data-ft-info-item-id] in window.SKILL_CATALOG). A plain
 *  click rolls/queues the skill via useAbility — the SAME single-arg global the full Skills tab emits. */
function skillChip(variable: string, entry: SkillEntry, canUse: boolean): string {
    const itemId = String(entry.item_id ?? '');
    const sid = escapeHtml(itemId);
    // Untrained (tier 0 / flawed) skills roll at DISADVANTAGE — flag them red and note it in the tooltip.
    const untrained = Number(entry.tier_level ?? 0) === 0;
    const name = skillNameFor(itemId, entry);
    const tipName = untrained ? `${name} · Untrained (rolls at Disadvantage)` : name;
    const cls = `ft-action-skill-chip${untrained ? ' ft-action-skill-chip--untrained' : ''}`;
    const icon = iconPh(variable || itemId, 30, 30, true, tipName);
    const hov = skillChipHoverBadges(entry);
    if (canUse) {
        return `<button type="button" class="${cls}" ${skillUseAttrs(itemId)} data-ft-info-item-id="${sid}">${hov}${icon}</button>`;
    }
    return `<div class="${cls} ft-action-skill-chip--ro" role="img" aria-label="${escapeHtml(tipName)}">${hov}${icon}</div>`;
}

/** The action-bar Skills tray inner HTML: the selected type's chips folded out on TOP, the skill-type selector
 *  row on the BOTTOM (ending in a divider + a "Show all" toggle). `activeType` falls back to innate (then the
 *  first available) when it has no skills in the current view. When `showAll`, UNTRAINED (tier-0) skills are
 *  included too — flagged red since they roll at disadvantage. '' when the caster has no skills at all. */
export function renderActionBarSkillsTray(caster: CachedCaster, casterId: string, activeType: string, showAll = false): string {
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const isPc = isPlayerCharacter(caster);
    const canUse = !isPc || canEdit; // render_skill_icon_button $can_use gate (non-PC bypasses permission)

    const byType = skillsByType(caster, showAll);
    const types = availableSkillTypes(byType);
    if (types.length === 0) return '';
    const eff = types.includes(activeType) ? activeType : (types.includes(DEFAULT_SKILL_TYPE) ? DEFAULT_SKILL_TYPE : types[0]!);
    const cid = escapeHtml(casterId);

    // Fold-out (TOP): the selected type's skill chips.
    let chips = '';
    for (const [variable, entry] of (byType.get(eff) ?? [])) chips += skillChip(variable, entry, canUse);
    const flyout = `<div class="ft-action-skills-flyout">${chips}</div>`;

    // Type selector (BOTTOM): one icon button per available type, the active one highlighted.
    let typeBtns = '';
    for (const t of types) {
        const label = SKILL_TYPE_LABELS[t] ?? t;
        const icon = SKILL_TYPE_ICON[t] ?? t;
        typeBtns += `<button type="button" class="ft-action-skilltype${t === eff ? ' is-active' : ''}"`
            + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}"`
            + ` ${skillAttrs('action-bar-skill-type', { casterId: cid, skillType: t })}>${iconPh(icon, 22, 22, false)}</button>`;
    }
    // …then a divider + the "Show all skills" toggle (reveals untrained/flawed skills, which roll at DIS).
    const allLabel = showAll ? 'Hide untrained skills' : 'Show all skills';
    const showAllBtn = `<button type="button" class="ft-action-skilltype ft-action-skilltype--all${showAll ? ' is-active' : ''}"`
        + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(allLabel)}" aria-label="${escapeHtml(allLabel)}"`
        + ` ${skillAttrs('action-bar-show-all', { casterId: cid })}>${iconPh('all_skills_list', 22, 22, false)}</button>`;
    const typeRow = `<div class="ft-action-skilltypes">${typeBtns}<span class="ft-action-skilltypes__divider" aria-hidden="true"></span>${showAllBtn}</div>`;

    return flyout + typeRow;
}

export function renderSkills(caster: CachedCaster, casterId: string, mount?: HTMLElement): string {
    // List edit gate = has_player_permission only (PHP $ft_pc_has_player_edit).
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    // SP columns are PLAYER-only. Gate on item_type === 'player_character' (the authoritative
    // discriminator: a PC token IN an encounter is still item_type player_character → keeps its SP;
    // a monster / SUMMON / encounter creature is item_type 'creature' → no SP, no to-next-tier. This is
    // the creature-vs-PC "carve out" — the skills logic is NOT 1:1 between the two.
    // MUST use isPlayerCharacter() — `ftItemsCol(caster,'item_type')` returns undefined on a caster blob
    // (item_type lives in encounter_creatures) so the old inline gate silently dropped SP for every PC.
    const isPc = isPlayerCharacter(caster);
    // Grid use gate = non-player-character caster bypasses permission (render_skill_icon_button $can_use).
    const canUseGrid = !isPc || canEdit;

    // Attr-safe, unique-per-mount suffix so two panes for the SAME caster never collide on ids; it is
    // also the first arg to the existing selectCharacterSkillsFilter() global (matches the
    // #div_all_skills_icons{sfx} / #div_table_skill_list{sfx} ids this render emits).
    let seq = mount ? mountSeq.get(mount) : undefined;
    if (seq === undefined) { seq = ++paneSeq; if (mount) mountSeq.set(mount, seq); }
    const sfx = '_' + String(casterId).replace(/[^A-Za-z0-9_]/g, '') + '_' + seq;

    let nav = '';
    for (let i = 0; i < NAV_TABS.length; i++) {
        const t = NAV_TABS[i];
        if (!t) continue;
        const active = i === 0 ? ' active' : '';
        nav += `<li class="nav-item" role="presentation"><button class="nav-link${active}" id="skills_tab_${t.mode}${sfx}" data-bs-toggle="tab" type="button" role="tab" aria-selected="${i === 0 ? 'true' : 'false'}" ${skillAttrs('filter', { suffix: sfx, mode: t.mode })}>${iconPh(t.icon)}</button></li>`;
    }

    return `<div class="row m-0 mb-2 pb-2 ml-1 mr-1 mt-2"><div class="col-lg-12 pl-2 pr-2"><div class="text-left">`
        + `<ul class="nav nav-tabs mb-2" id="full_skills_side_info_tabs_nav${sfx}" role="tablist">${nav}</ul>`
        + iconGrid(caster, sfx, canUseGrid)
        + detailList(caster, casterId, sfx, canEdit, isPc)
        + `</div></div></div>`;
}

/** A grant edit replaces only its displayed rows/buttons, preserving filters and siblings. */
export function patchSkillGrantRows(caster: CachedCaster, casterId: string, mount: HTMLElement, variables: readonly string[]): void {
    const entries = new Map(skillEntries(caster));
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    for (const variable of variables) {
        const entry = entries.get(variable);
        if (!entry) continue;
        for (const row of mount.querySelectorAll<HTMLElement>('[data-ft-skill-variable]')) {
            if (row.dataset.ftSkillVariable !== variable) continue;
            const template = document.createElement('template');
            template.innerHTML = listRow(caster, casterId, variable, entry, canEdit, isPlayerCharacter(caster));
            const replacement = template.content.firstElementChild;
            if (replacement) row.replaceWith(replacement);
        }
        // The icon grid has no entry at tier zero. Its category wrapper remains
        // held; append/remove just the affected button when training changes.
        const id = String(entry.item_id ?? '');
        const group = [...mount.querySelectorAll<HTMLElement>('[data-ft-skill-group]')]
            .find(group => group.dataset.ftSkillGroup === skillTypeFor(id, entry))?.querySelector('.ft-skill-group-skills');
        if (!group) continue;
        const previous = [...group.querySelectorAll<HTMLElement>('[data-ft-skill-grid-variable]')]
            .find(button => button.dataset.ftSkillGridVariable === variable);
        const template = document.createElement('template');
        template.innerHTML = Number(entry.tier_level ?? 0) > 0 ? gridSkillButton(variable, entry, !isPlayerCharacter(caster) || canEdit) : '';
        const replacement = template.content.firstElementChild;
        if (previous && replacement) previous.replaceWith(replacement);
        else if (previous) previous.remove();
        else if (replacement) group.append(replacement);
    }
    window.ftMountIcons?.(mount);
}
